/*
 * File: WeSpeakPrivacyPolicy.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 9, 2025 at 5:09 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 31, 2025 at 10:13 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// Components
import {
	Meta,
	AppNavigation,
	LegalNavigation,
	Typography,
	LocaleLink,
} from '../../../../../src/components';
import { HighlightSection } from '../../../../../src/components/LegalNavigation/LegalNavigation.styles';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../../constants/base.constants';
import { LEGAL_NAVIGATION_SECTIONS } from '../../Constants';

// Styles
import * as S from './WeSpeakPrivacyPolicy.styles';


/**
 * Component
 */

const WeSpeakPrivacyPolicy = ({ meta: m }) => {

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageWeSpeakPrivacyPolicy.metaTitle', { partnerName: partner.name }),
		description: t('pageWeSpeakPrivacyPolicy.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Create navigation sections
	const navigationSections = useMemo(() => LEGAL_NAVIGATION_SECTIONS(locale), [locale]);

	// Handle actions on component load
	useEffect(() => {}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Component Content */}
			<AppNavigation showFooter contentPadding={false}>
				<LegalNavigation
					navigationSections={navigationSections}
					mobileNavigationHeight={440}
				>
					<S.Wrapper>

						{/* Title */}
						<Typography tag="h1" weight="bold">
							We-Speak Privacy Policy
						</Typography>

						{/* TL;DR */}
						<HighlightSection>
							<Typography tag="p">
								The We-Speak platform is powered by
								{' '}
								<LocaleLink
									title="Our Wave"
									aria-label="Our Wave"
									target="_blank"
									to="https://www.ourwave.org"
									rel="noreferrer"
								>
									Our Wave
								</LocaleLink>
								, a nonprofit
								organization dedicated to building meaningful technology to empower survivors of sexual violence. For more
								information about our technology, team, or partnership opportunities, please
								{' '}
								<LocaleLink
									title="Visit Our Website"
									aria-label="Visit Our Website"
									target="_blank"
									to="https://www.ourwave.org"
									rel="noreferrer"
								>
									visit our website
								</LocaleLink>
								.
							</Typography>
						</HighlightSection>

						{/* Introduction */}
						<Typography tag="p">
							Dublin Rape Crisis Centre (DRCC) takes security and privacy seriously. This notice relates to our privacy practices
							in connection with this website, the platform on which we host your stories
							(
							<b>Platform</b>
							). While we may link to third party websites, we are not responsible for the content or privacy
							practices on those other sites.
						</Typography>
						<br />

						{/* What information do we collect? */}
						<Typography tag="h2" weight="bold">
							What information do we collect?
						</Typography>
						<Typography tag="p">
							When we say &quot;personal data&quot;, we mean any information that can identify an individual, such as their name, address,
							date of birth, etc.
						</Typography>
						<Typography tag="p">
							We collect your personal data in the following ways:
						</Typography>
						<ul>
							<li>
								Where you provide us with your personal data, for example, through our Platform, by submitting your
								story (
								<b>Story</b>
								).
							</li>
							<li>
								We collect certain information automatically when you visit our website (
								<b>Platform user data</b>
								).
							</li>
						</ul>
						<br />

						{/* How we use personal data we collect */}
						<Typography tag="h2" weight="bold">
							How we use personal data we collect
						</Typography>
						<Typography tag="p">
							On each of our online forms, there will be further details of why we are collecting the information asked for.
						</Typography>
						<Typography tag="p">
							We store and process your personal data as follows:
						</Typography>
						<S.TableOverflow>
							<S.Table>
								<thead>
									<tr>
										<th>Data we Collect</th>
										<th>Purpose(s) for Processing</th>
										<th>Legal Basis</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>
											<Typography tag="p">
												Your email address
											</Typography>
										</td>
										<td>
											<ul>
												<li>
													To allow you to have control over your Story. When you attach an email address to your Story,
													you can log in and finish a draft Story, or you can delete a submitted Story. We also need
													your email so we can tell you when your story has been posted, and to provide resources
													and support.
												</li>
												<li>
													When you opt in to be notified about DRCC updates, events, and ways to help out.
												</li>
												<li>
													When you opt in to receive our partners&apos; newsletters.
												</li>
											</ul>
										</td>
										<td>
											<ul>
												<li>
													Where necessary for performance of contract concluded with you (when you use the services
													provided on the website, you agree to our terms and conditions).
												</li>
												<li>
													In our legitimate interests in providing an online Platform.
												</li>
												<li>
													Where you provide explicit consent.
												</li>
											</ul>
										</td>
									</tr>
									<tr>
										<td>
											<Typography tag="p">
												The contents of your Story, which may include your gender, age, race, sexual orientation and
												any other personal or sensitive personal data which you wish to provide.
											</Typography>
											<Typography tag="p">
												<i>
													Please note that we will remove any identifying information from your Story before publishing,
													such that any personal data will only be visible to our administrators, and not to other
													Platform visitors and users.
												</i>
											</Typography>
										</td>
										<td>
											<ul>
												<li>
													To allow you to anonymously submit your Story.
												</li>
												<li>
													To share with our NGO partner, Our Wave Inc. for publishing on their platform for survivors&apos; stories.
												</li>
											</ul>
										</td>
										<td>
											<ul>
												<li>
													Where necessary for performance of contract concluded with you (when you use the services
													provided on the website, you agree to our terms and conditions).
												</li>
												<li>
													Where you provide explicit consent.
												</li>
											</ul>
										</td>
									</tr>
									<tr>
										<td>
											<Typography tag="p">
												(Optional) Your ethnic / racial identity
											</Typography>
										</td>
										<td>
											<ul>
												<li>
													To allow us to categorise your Story. Categories can help survivors with similar experiences
													to find your Story.
												</li>
											</ul>
										</td>
										<td>
											<ul>
												<li>
													Where you provide explicit consent
												</li>
											</ul>
										</td>
									</tr>
									<tr>
										<td>
											<Typography tag="p">
												(Optional) Your sexual orientation
											</Typography>
										</td>
										<td>
											<ul>
												<li>
													To allow us to categorise your Story. Categories can help survivors with similar experiences
													to find your Story.
												</li>
											</ul>
										</td>
										<td>
											<ul>
												<li>
													Where you provide explicit consent
												</li>
											</ul>
										</td>
									</tr>
									<tr>
										<td>
											<Typography tag="p">
												(Optional) Your gender identity
											</Typography>
										</td>
										<td>
											<ul>
												<li>
													To allow us to categorise your Story. Categories can help survivors with similar experiences
													to find your Story.
												</li>
											</ul>
										</td>
										<td>
											<ul>
												<li>
													Where you provide explicit consent.
												</li>
											</ul>
										</td>
									</tr>
									<tr>
										<td>
											<Typography tag="p">
												(Optional) Your age
											</Typography>
										</td>
										<td>
											<ul>
												<li>
													To allow us to categorise your Story. Categories can help survivors with similar experiences
													to find your Story.
												</li>
											</ul>
										</td>
										<td>
											<ul>
												<li>
													Where you provide explicit consent.
												</li>
												<li>
													In our legitimate interests in providing an online Platform.
												</li>
											</ul>
										</td>
									</tr>
									<tr>
										<td>
											<Typography tag="p">
												Technical information (information about your usage of the website)
											</Typography>
										</td>
										<td>
											<ul>
												<li>
													To enable website functionality, enable features, implement security measures, and provide
													a reliable service.
												</li>
												<li>
													To analyse our website analytics.
												</li>
											</ul>
										</td>
										<td>
											<ul>
												<li>
													Where necessary for performance of contract concluded with you (when you use the services
													provided on the website, you agree to our terms and conditions).
												</li>
												<li>
													In our legitimate interests in providing an online Platform.
												</li>
												<li>
													In the legitimate interests of visitors to, and users of, our online Platform.
												</li>
											</ul>
										</td>
									</tr>
								</tbody>
							</S.Table>
						</S.TableOverflow>
						<br />
						<br />

						{/* Who do we share your information with? */}
						<Typography tag="h2" weight="bold">
							Who do we share your information with?
						</Typography>
						<Typography tag="p">
							We want you to understand when and with whom we may share your personal data:
						</Typography>
						<ul>
							<li>
								<span className="underline">Third-party service providers:</span>
								{' '}
								We will share your information with third-party
								service-providers who provide services on our behalf. Our Platform provider and our internet service provider
								will only process your data for specified purposes and in accordance with our instructions.
							</li>
							<li>
								<span className="underline">Legal purposes:</span>
								{' '}
								We may be required to share your information in order to comply
								with and respond to any requests from law enforcement or regulatory agencies.
							</li>
							<li>
								<span className="underline">Security Purposes:</span>
								{' '}
								We may share your information as we believe necessary, in order
								to secure and protect the rights, interests, safety, and security of our site, our users, and others against
								security incidents.
							</li>
							<li>
								<span className="underline">Harm Prevention:</span>
								{' '}
								We may share your information as we believe necessary, in order
								to protect the health and safety of our users or another natural person.
							</li>
						</ul>
						<br />

						{/* Cross-border data transfers */}
						<Typography tag="h2" weight="bold">
							Cross-border data transfers
						</Typography>
						<Typography tag="p">
							Certain limited categories of your personal data that we collect from you may be transferred to, and stored at, a
							destination outside the European Economic Community (&quot;
							<b>EEA</b>
							&quot;).  We will ensure appropriate safeguards are in
							place to protect the transfer of your personal data to these non-EEA countries, including Standard Contractual
							Clauses under Article 46(2) GDPR. To obtain more information concerning these safeguards, see Contact Us below.
						</Typography>
						<br />

						{/* How long do we store your data for? */}
						<Typography tag="h2" weight="bold">
							How long do we store your data for?
						</Typography>
						<Typography tag="p">
							We will store your personal data only for as long as necessary for the purposes of providing access to our Platform
							and related services to you; as required by law or regulatory guidance to which we are subject, and for the
							exercise or defence of legal claims that may be brought by or against us.
						</Typography>
						<Typography tag="p">
							Although we will do our best to protect your personal data, we cannot guarantee the security of your personal data
							transmitted to our website. Any transmission of personal data is at your own risk. Once we receive your personal
							data, we use appropriate security measures to seek to prevent unauthorised access or disclosure.
						</Typography>
						<br />

						{/* What are your rights? */}
						<Typography tag="h2" weight="bold">
							What are your rights?
						</Typography>
						<Typography tag="p">
							Under the European Union General Data Protection Regulation (
							<b>GDPR</b>
							), you have a number of rights with respect to
							your personal data, subject to any exemptions provided by the law. The following is a summary of your rights:
						</Typography>
						<ul>
							<li>
								<b>The right of access</b>
								{' '}
								enables you to receive a copy of your personal data.
							</li>
							<li>
								<b>The right to rectification</b>
								{' '}
								enables you to correct any inaccurate or incomplete personal data we hold
								about you.
							</li>
							<li>
								<b>The right to erasure</b>
								{' '}
								enables you to ask us to delete your personal data in certain circumstances.
							</li>
							<li>
								<b>The right to restrict processing</b>
								{' '}
								enables you to ask us to halt the processing of your personal data in
								certain circumstances.
							</li>
							<li>
								<b>The right to object</b>
								{' '}
								enables you to object to us processing your personal data on the basis of our
								legitimate interests (or those of a third party), including processing for direct marketing purposes or where
								we are performing a task in the public interest. Your objection will be upheld, and we will cease processing
								your personal data, unless the processing is based on compelling legitimate grounds or is needed for the
								exercise or defence of legal claims that may be brought by or against us.
							</li>
							<li>
								<b>The right to data portability</b>
								{' '}
								enables you to request us to transmit personal data that you have provided
								to us, to a third party without hindrance, or to give you a copy of it so that you can transmit it to a
								third party, where technically feasible.
							</li>
							<li>
								When we process personal data you provide to us based on your consent, you have
								{' '}
								<b>
									the right to withdraw
									your consent
								</b>
								{' '}
								at any time, without affecting the lawfulness of processing based on consent before
								its withdrawal.
							</li>
						</ul>
						<Typography tag="p">
							To exercise any of the above mentioned rights, contact us as described in &quot;Contact Us&quot; section below.
						</Typography>
						<Typography tag="p">
							In addition to this, you may make a complaint about our data processing activities to a supervisory
							authority. The supervisory authority for DRCC is the Data Protection Commissioner in Ireland
							(
							<LocaleLink title="https://www.dataprotection.ie" aria-label="https://www.dataprotection.ie" to="https://www.dataprotection.ie" target="_blank" rel="noreferrer">https://www.dataprotection.ie</LocaleLink>
							).
						</Typography>
						<br />

						{/* Changes to this Privacy Notice */}
						<Typography tag="h2" weight="bold">
							Changes to this Privacy Notice
						</Typography>
						<Typography tag="p">
							We reserve the right to change this Privacy Notice from time to time at our sole discretion. If we make any
							changes, we will post those changes here and update the “Last Updated” date at the bottom of this Privacy
							Notice. However, if we make material changes to this Privacy Notice, we will notify you thereof prior to the
							change becoming effective.
						</Typography>
						<Typography tag="p">
							This Privacy Notice was last updated on 31 May 2025.
						</Typography>
						<br />

						{/* Contact us */}
						<Typography tag="h2" weight="bold">
							Contact us
						</Typography>
						<Typography tag="p">
							If you have any queries relating to this Privacy Noice or our use of your personal data, please contact us via
							email at
							{' '}
							<LocaleLink title="info@rcc.ie" aria-label="info@rcc.ie" to="mailto:info@rcc.ie?Subject=Request">info@rcc.ie</LocaleLink>
							, or in writing to:
						</Typography>

						{/* Address */}
						<Typography tag="p">
							Data Protection Officer,
							<br />
							DRCC,
							<br />
							70 Lower Leeson Street,
							<br />
							Dublin 2,
							<br />
							D02 VW13, Ireland
						</Typography>

					</S.Wrapper>
				</LegalNavigation>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

WeSpeakPrivacyPolicy.propTypes = {
	meta: PropTypes.shape()
};
WeSpeakPrivacyPolicy.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default WeSpeakPrivacyPolicy;
